import { useEffect, useMemo, useState, type CSSProperties, type DragEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  launchesApi,
  notificationsApi,
  tasksApi,
  taskTypesApi,
  usersApi,
} from "../../api/endpoints";
import { IcoChevLeft, IcoPlus } from "../../components/icons";
import { useCanWriteTasks } from "../../hooks/useTaskPermissions";
import { useSession } from "../../hooks/useSession";
import { useSettings } from "../../hooks/useSettings";
import {
  buildWeek,
  formatWeekRange,
  isSameWeek,
  parseIsoLocal,
  todayIsoLocal,
  toLocalIsoDate,
} from "../../lib/weekDates";
import { TaskColumn } from "./TaskColumn";
import { TaskModal } from "./TaskModal";
import { TasksRisksView } from "./TasksRisksView";
import { TasksAllView } from "./TasksAllView";
import { usePanScroll } from "../../hooks/usePanScroll";
import type { Task } from "../../types/domain";

type Mode = "week" | "risks" | "all";

const MODES: Array<{ id: Mode; label: string }> = [
  { id: "week", label: "Неделя" },
  { id: "risks", label: "Риски" },
  { id: "all", label: "Все задачи" },
];

// Wrapper-контейнер ограничивает ширину физически — нативный <select> внутри
// не может стать шире, независимо от длины option-ов или особенностей Chrome.
const FILTER_SHELL_STYLE: CSSProperties = {
  display: "inline-block",
  width: 200,
  flex: "0 0 200px",
};
// select внутри shell — width: 100% от контейнера. !important через
// inline-style спецификации (инлайн = 1000, перебивает любой кэшированный CSS).
const FILTER_SELECT_STYLE: CSSProperties = {
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  flex: "0 0 auto",
};

// Нативный <select> в Chrome берёт ширину по самой длинной option и игнорирует
// max-width на самом select-е. Поэтому жёстко обрезаем подписи в опциях, чтобы
// селекты физически не могли растянуться больше нашего лимита независимо от
// CSS, кэша и плотности дисплея.
const OPTION_LABEL_LIMIT = 22;
function truncateLabel(text: string): string {
  if (text.length <= OPTION_LABEL_LIMIT) return text;
  return text.slice(0, OPTION_LABEL_LIMIT - 1) + "…";
}

type ModalState =
  | { kind: "closed" }
  | { kind: "create"; plannedDate?: string | null }
  | { kind: "edit"; task: Task };

export function TasksPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const canWrite = useCanWriteTasks();
  const me = useSession().data ?? null;
  const { settings, update: updateSettings } = useSettings();
  const activeLaunchId = settings?.activeLaunchId ?? null;
  const tasksFilter = settings?.tasksFilter ?? "all";
  const tasksAssignee = settings?.tasksAssignee ?? "all";
  const tasksCreator = settings?.tasksCreator ?? "all";
  const qc = useQueryClient();
  const [mode, setMode] = useState<Mode>("week");
  const [anchor, setAnchor] = useState<Date>(() => parseIsoLocal(todayIsoLocal()));
  const [modal, setModal] = useState<ModalState>({ kind: "closed" });
  const [draggingTaskId, setDraggingTaskId] = useState<string | null>(null);
  const { onMouseDown: onBoardMouseDown, panning } = usePanScroll();

  const week = useMemo(() => buildWeek(anchor), [anchor]);
  const today = parseIsoLocal(todayIsoLocal());
  const isCurrentWeek = isSameWeek(anchor, today);

  const fromIso = week[0]!.iso;
  const toIso = week[week.length - 1]!.iso;
  const tasksKey = ["tasks", "week", fromIso, toIso] as const;

  const tasksQuery = useQuery({
    queryKey: tasksKey,
    queryFn: async () =>
      (await tasksApi.list({ from: fromIso, to: toIso, includeUndated: true })).tasks,
    staleTime: 5_000,
  });
  const openTaskId = searchParams.get("taskId");
  const openNotificationId = searchParams.get("notificationId");
  const directTaskQuery = useQuery({
    queryKey: ["tasks", "direct", openTaskId],
    queryFn: async () => (await tasksApi.get(openTaskId!)).task,
    enabled: !!openTaskId,
    retry: false,
  });

  useEffect(() => {
    if (!directTaskQuery.data || !openTaskId) return;
    setModal({ kind: "edit", task: directTaskQuery.data });
    if (openNotificationId) {
      notificationsApi.markRead(openNotificationId)
        .then(() => qc.invalidateQueries({ queryKey: ["notifications"] }))
        .catch(() => undefined);
    }
    const next = new URLSearchParams(searchParams);
    next.delete("taskId");
    next.delete("notificationId");
    setSearchParams(next, { replace: true });
  }, [directTaskQuery.data, openNotificationId, openTaskId, qc, searchParams, setSearchParams]);

  const taskTypesQuery = useQuery({
    queryKey: ["taskTypes"],
    queryFn: async () => (await taskTypesApi.list()).types,
    staleTime: 60_000,
  });
  const taskTypes = taskTypesQuery.data ?? [];

  const launchesQuery = useQuery({
    queryKey: ["launches"],
    queryFn: async () => (await launchesApi.list()).launches,
    staleTime: 30_000,
  });
  const launches = launchesQuery.data ?? [];

  const usersQuery = useQuery({
    queryKey: ["users"],
    queryFn: async () => (await usersApi.list()).users,
    staleTime: 60_000,
  });
  const users = usersQuery.data ?? [];
  const activeLaunchColor = useMemo(() => {
    if (!activeLaunchId) return null;
    const l = launches.find((x) => x.id === activeLaunchId);
    return l?.color ?? null;
  }, [launches, activeLaunchId]);

  /** Возвращает true, если задача проходит текущий фильтр «Запуск».
   *  "all" → все, "active" → только activeLaunchId, "none" → без launchId,
   *  иначе значение трактуется как launch.id. */
  const launchFilterFn = useMemo(() => {
    return (t: Task): boolean => {
      if (tasksFilter === "all") return true;
      if (tasksFilter === "none") return !t.launchId;
      if (tasksFilter === "active") {
        if (!activeLaunchId) return true; // если активный запуск не выбран — не фильтруем
        return t.launchId === activeLaunchId;
      }
      return t.launchId === tasksFilter;
    };
  }, [tasksFilter, activeLaunchId]);

  /** Фильтр «Ответственный». "all" → все, "me" → assignee == текущий юзер,
   *  "unassigned" → без ответственного, иначе значение трактуется как user.id. */
  const assigneeFilterFn = useMemo(() => {
    return (t: Task): boolean => {
      if (tasksAssignee === "all") return true;
      if (tasksAssignee === "unassigned") return !t.assigneeId && !t.assigneeName;
      if (tasksAssignee === "me") {
        if (!me?.id) return true; // если сессии нет — не фильтруем (страховка)
        return t.assigneeId === me.id;
      }
      return t.assigneeId === tasksAssignee;
    };
  }, [tasksAssignee, me?.id]);

  /** Фильтр «Постановщик» по Task.createdById. Семантика та же, что у assignee. */
  const creatorFilterFn = useMemo(() => {
    return (t: Task): boolean => {
      if (tasksCreator === "all") return true;
      if (tasksCreator === "unassigned") return !t.createdById;
      if (tasksCreator === "me") {
        if (!me?.id) return true;
        return t.createdById === me.id;
      }
      return t.createdById === tasksCreator;
    };
  }, [tasksCreator, me?.id]);

  const taskFilterFn = useMemo(
    () => (t: Task) => launchFilterFn(t) && creatorFilterFn(t) && assigneeFilterFn(t),
    [launchFilterFn, creatorFilterFn, assigneeFilterFn],
  );

  const tasksByDay = useMemo(() => {
    const map = new Map<string, Task[]>();
    for (const day of week) map.set(day.iso, []);
    const undated: Task[] = [];
    for (const task of tasksQuery.data ?? []) {
      if (!taskFilterFn(task)) continue;
      if (!task.plannedDate) {
        undated.push(task);
        continue;
      }
      const bucket = map.get(task.plannedDate);
      if (bucket) bucket.push(task);
    }
    for (const list of map.values()) list.sort(byPosition);
    undated.sort(byPosition);
    return { byDay: map, undated };
  }, [week, tasksQuery.data, taskFilterFn]);

  const moveMutation = useMutation({
    mutationFn: (vars: { id: string; plannedDate: string | null; position: number }) =>
      tasksApi.move(vars.id, { plannedDate: vars.plannedDate, position: vars.position }),
    onSettled: () => qc.invalidateQueries({ queryKey: ["tasks"] }),
  });

  const handleDrop = (
    plannedDate: string | null,
    targetIndex: number,
    _event: DragEvent<HTMLDivElement>,
  ) => {
    if (!draggingTaskId) return;
    const id = draggingTaskId;
    setDraggingTaskId(null);
    moveMutation.mutate({ id, plannedDate, position: targetIndex });
  };

  const goPrev = () => setAnchor((d) => addDays(d, -7));
  const goNext = () => setAnchor((d) => addDays(d, 7));
  const goToday = () => setAnchor(parseIsoLocal(todayIsoLocal()));

  const onPickDate = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.value) return;
    setAnchor(parseIsoLocal(e.target.value));
  };

  const filteredAll = useMemo(
    () => (tasksQuery.data ?? []).filter(taskFilterFn),
    [tasksQuery.data, taskFilterFn],
  );

  // «Я» в селекте идёт первым; остальные — отсортированы сервером по name/email.
  const otherUsers = useMemo(
    () => users.filter((u) => u.id !== me?.id),
    [users, me?.id],
  );
  const totalTasks = filteredAll.length;
  const isLoading = tasksQuery.isLoading;
  const isError = tasksQuery.isError;

  return (
    <>
      <div className="board-head tasks-head">
        <div className="board-title-wrap">
          <h1>Задачи</h1>
          <div className="board-subtitle">
            Управление командой
            {!isLoading && !isError && (
              <>
                {" · "}
                <b>{totalTasks}</b> {pluralTasks(totalTasks)} на этой неделе
              </>
            )}
          </div>
        </div>
        <div className="tasks-head-actions">
          <div className="tasks-mode-tabs" role="tablist">
            {MODES.map((m) => (
              <button
                key={m.id}
                type="button"
                role="tab"
                aria-selected={mode === m.id}
                className={"tasks-mode-tab" + (mode === m.id ? " is-active" : "")}
                onClick={() => setMode(m.id)}
              >
                {m.label}
              </button>
            ))}
          </div>
          {canWrite && (
            <button
              type="button"
              className="btn-primary tasks-new-btn"
              onClick={() => setModal({ kind: "create" })}
            >
              <IcoPlus size={14} /> Новая задача
            </button>
          )}
        </div>
      </div>

      <div className="tasks-filters">
        <label className="tasks-filter">
          <span>Запуск</span>
          <span className="filter-select-shell" style={FILTER_SHELL_STYLE}>
          <select
            value={tasksFilter}
            onChange={(e) => updateSettings({ tasksFilter: e.target.value })}
            style={FILTER_SELECT_STYLE}
          >
            <option value="all">Все</option>
            <option value="active" disabled={!activeLaunchId}>
              {activeLaunchId ? "Только активный" : "Только активный (не выбран)"}
            </option>
            <option value="none">Без запуска</option>
            {launches.length > 0 && <option disabled>──────────</option>}
            {launches.map((l) => (
              <option key={l.id} value={l.id} title={l.name}>
                {truncateLabel(l.name)}
              </option>
            ))}
          </select>
          </span>
        </label>
        <label className="tasks-filter">
          <span>Постановщик</span>
          <span className="filter-select-shell" style={FILTER_SHELL_STYLE}>
          <select
            value={tasksCreator}
            onChange={(e) => updateSettings({ tasksCreator: e.target.value })}
            style={FILTER_SELECT_STYLE}
          >
            <option value="all">Все</option>
            {me && (
              <option value="me" title={me.name ?? me.email ?? "Я"}>
                {truncateLabel(`Я${me.name ? ` (${me.name})` : me.email ? ` (${me.email})` : ""}`)}
              </option>
            )}
            <option value="unassigned">Без постановщика</option>
            {otherUsers.length > 0 && <option disabled>──────────</option>}
            {otherUsers.map((u) => (
              <option key={u.id} value={u.id} title={u.name || u.email}>
                {truncateLabel(u.name || u.email)}
              </option>
            ))}
          </select>
          </span>
        </label>
        <label className="tasks-filter">
          <span>Ответственный</span>
          <span className="filter-select-shell" style={FILTER_SHELL_STYLE}>
          <select
            value={tasksAssignee}
            onChange={(e) => updateSettings({ tasksAssignee: e.target.value })}
            style={FILTER_SELECT_STYLE}
          >
            <option value="all">Все</option>
            {me && (
              <option value="me" title={me.name ?? me.email ?? "Я"}>
                {truncateLabel(`Я${me.name ? ` (${me.name})` : me.email ? ` (${me.email})` : ""}`)}
              </option>
            )}
            <option value="unassigned">Без ответственного</option>
            {otherUsers.length > 0 && <option disabled>──────────</option>}
            {otherUsers.map((u) => (
              <option key={u.id} value={u.id} title={u.name || u.email}>
                {truncateLabel(u.name || u.email)}
              </option>
            ))}
          </select>
          </span>
        </label>
      </div>

      {mode === "week" && (
        <>
          <div className="tasks-week-nav">
            <div className="tasks-week-controls">
              <button
                type="button"
                className="tasks-week-btn"
                onClick={goPrev}
                aria-label="Предыдущая неделя"
              >
                <IcoChevLeft size={14} sw={2.2} />
              </button>
              <button
                type="button"
                className={"tasks-week-today" + (isCurrentWeek ? " is-current" : "")}
                onClick={goToday}
                title="Текущая неделя"
              >
                Сегодня
              </button>
              <button
                type="button"
                className="tasks-week-btn tasks-week-btn-next"
                onClick={goNext}
                aria-label="Следующая неделя"
              >
                <IcoChevLeft size={14} sw={2.2} />
              </button>
              <div className="tasks-week-range">{formatWeekRange(week)}</div>
            </div>
            <div className="tasks-week-right">
              <label className="tasks-week-jump">
                <span>Перейти к дате</span>
                <input type="date" value={toLocalIsoDate(anchor)} onChange={onPickDate} />
              </label>
            </div>
          </div>

          {isError && (
            <div className="tasks-stub" style={{ color: "#DC2626" }}>
              Не удалось загрузить задачи. Перезагрузите страницу.
            </div>
          )}

          <div
            className={
              "board-scroll tasks-board-scroll" + (panning ? " is-panning" : "")
            }
            onMouseDown={onBoardMouseDown}
          >
            <div className="board">
              {week.map((day) => (
                <TaskColumn
                  key={day.iso}
                  day={day}
                  tasks={tasksByDay.byDay.get(day.iso) ?? []}
                  taskTypes={taskTypes}
                  draggingTaskId={draggingTaskId}
                  canWrite={canWrite}
                  meId={me?.id ?? null}
                  meRole={me?.role ?? null}
                  activeLaunchId={activeLaunchId}
                  activeLaunchColor={activeLaunchColor}
                  onOpen={(t) => setModal({ kind: "edit", task: t })}
                  onAdd={() => setModal({ kind: "create", plannedDate: day.iso })}
                  onDragStart={setDraggingTaskId}
                  onDragEnd={() => setDraggingTaskId(null)}
                  onDrop={handleDrop}
                />
              ))}
              <TaskColumn
                day={null}
                tasks={tasksByDay.undated}
                taskTypes={taskTypes}
                draggingTaskId={draggingTaskId}
                canWrite={canWrite}
                meId={me?.id ?? null}
                meRole={me?.role ?? null}
                activeLaunchId={activeLaunchId}
                activeLaunchColor={activeLaunchColor}
                onOpen={(t) => setModal({ kind: "edit", task: t })}
                onAdd={() => setModal({ kind: "create", plannedDate: null })}
                onDragStart={setDraggingTaskId}
                onDragEnd={() => setDraggingTaskId(null)}
                onDrop={handleDrop}
              />
            </div>
          </div>
        </>
      )}

      {mode === "risks" && (
        <TasksRisksView
          launchFilter={taskFilterFn}
          onOpen={(t) => setModal({ kind: "edit", task: t })}
        />
      )}

      {mode === "all" && (
        <TasksAllView
          launchFilter={taskFilterFn}
          onOpen={(t) => setModal({ kind: "edit", task: t })}
        />
      )}

      {modal.kind === "create" && (
        <TaskModal
          mode="create"
          seed={{ plannedDate: modal.plannedDate ?? null }}
          onClose={() => setModal({ kind: "closed" })}
        />
      )}
      {modal.kind === "edit" && (
        <TaskModal
          mode="edit"
          task={modal.task}
          onClose={() => setModal({ kind: "closed" })}
        />
      )}
    </>
  );
}

function byPosition(a: Task, b: Task): number {
  if (a.position !== b.position) return a.position - b.position;
  return a.createdAt.localeCompare(b.createdAt);
}

function addDays(d: Date, days: number): Date {
  const out = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  out.setDate(out.getDate() + days);
  return out;
}

function pluralTasks(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "задача";
  if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) return "задачи";
  return "задач";
}
